import React from "react";
import {
  ColumnsModel,
  QueryBuilderComponent,
} from "@syncfusion/ej2-react-querybuilder";
import { useQueryBuilder, UseQueryBuilderHookProps } from "./useQueryBuilder";
import { DataManager } from "@syncfusion/ej2-data";
import { SimpleFilterField } from "./types";
import classNames from "classnames";
import { Button, Intent } from "@blueprintjs/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

export type QueryBuilderColumn = ColumnsModel | SimpleFilterField;

export interface QueryBuilderFieldProps extends UseQueryBuilderHookProps {
  dataSource?: DataManager;
  columns: QueryBuilderColumn[];
  // Filter panel mode props
  isFilterPanel?: boolean;
  hideFiltering?: boolean;
  title?: string;
  className?: string;
  displayMode?: "Vertical" | "Horizontal";
  filterPanelHeaderItems?: React.ReactNode;
}

// By design, this is a controlled component.
export const QueryBuilderField: React.FC<QueryBuilderFieldProps> = ({
  customQueryBuilderComponentProps,
  columns,
  onQueryChange,
  onSubmit,
  dataSource,
  value,
  isFilterPanel = false,
  hideFiltering = false,
  title = "Filters",
  className = "",
  displayMode = "Vertical",
  filterPanelHeaderItems,
}) => {
  const {
    queryBuilderComponentRef,
    queryBuilderComponentKey,
    ruleChange,
    queryBuilderColumns,
    rule,
    clearFilters,
    isQueryBuilderStatePotentiallyDifferentThanDefault,
  } = useQueryBuilder({
    customQueryBuilderComponentProps,
    value,
    onSubmit,
    onQueryChange,
    columns,
    dataSource,
  });

  return (
    <div
      className={classNames(
        {
          "ucl-query-builder-wrapper-simple": isFilterPanel,
        },
        "ucl-query-builder-wrapper",
        className
      )}
    >
      {isFilterPanel && (
        <div className="filter-panel-header">
          <h3 className="filter-panel-title">{title}</h3>
          <div className="filter-panel-actions">
            {isQueryBuilderStatePotentiallyDifferentThanDefault && (
              <Button
                className="clear-filters-button"
                intent={Intent.NONE}
                onClick={clearFilters}
                icon={<FontAwesomeIcon icon={faTimes} />}
                minimal
                small
              >
                Clear Filters
              </Button>
            )}
            {filterPanelHeaderItems && (
              <div className="filter-panel-header-items">
                {filterPanelHeaderItems}
              </div>
            )}
          </div>
        </div>
      )}
      {!hideFiltering && (
        <div className={isFilterPanel ? "filter-panel-content" : ""}>
          <QueryBuilderComponent
            {...customQueryBuilderComponentProps}
            key={queryBuilderComponentKey}
            ref={queryBuilderComponentRef}
            columns={queryBuilderColumns}
            dataSource={dataSource}
            ruleChange={ruleChange}
            rule={rule}
            enableNotCondition={false}
            displayMode={isFilterPanel ? "Vertical" : displayMode}
            cssClass={classNames("ucl-query-builder", {
              "ucl-query-builder-simple": isFilterPanel,
            })}
            showButtons={
              isFilterPanel
                ? {
                    ruleDelete: false,
                    cloneRule: false,
                    cloneGroup: false,
                    groupDelete: false,
                    groupInsert: false,
                    lockGroup: false,
                    lockRule: false,
                  }
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
};
